/**
 * 한 줄로 합쳐질 status 묶음의 키.
 *
 * status 행의 house_id는 등록 당시 선택돼 있던 HBL이라, 같은 컨테이너라도 HBL 이동·다른 HBL
 * 선택 후 등록 등으로 값이 갈릴 수 있다. house_id를 키에 넣으면 그 컨테이너가 두 줄로 나와
 * 컨테이너 수와 줄 수가 어긋나므로(OS-546), 컨테이너 status는 child_id만으로 묶는다.
 */
const bucketKeyOf = ({ house_id, child_id }) => (child_id === 0 ? `house:${house_id}` : `child:${child_id}`);

/** 정렬용 숫자 키. 값이 없는 행은 뒤로 보낸다. */
const numericOrder = (value) =>
    value === null || value === undefined || value === '' || Number.isNaN(Number(value))
        ? Number.POSITIVE_INFINITY
        : Number(value);

/**
 * 담당자가 Status 셀을 읽는 순서로 줄을 세운다.
 *
 * HBL(house_id) 묶음 순서대로, 각 묶음 안에서는 HBL 전체에 걸린 WARNING SIGN(child_id 0, 빨간 글씨)이
 * 맨 위에 오고 그 아래 컨테이너 줄이 이어진다. HBL이 둘이면 첫 HBL의 경고와 컨테이너가 모두 나온
 * 뒤 다음 HBL의 경고가 다시 빨간 글씨로 시작한다. 경고를 컨테이너 상태보다 나중에 등록해도
 * 이 순서는 바뀌지 않는다.
 */
export const sortStatusCellRows = (rows) =>
    [...rows].sort(
        (a, b) =>
            numericOrder(a.house_id) - numericOrder(b.house_id) ||
            numericOrder(a.child_id) - numericOrder(b.child_id) ||
            numericOrder(a.id) - numericOrder(b.id),
    );

/**
 * 메인 테이블 Status 셀에 보여줄 줄을 뽑는다.
 *
 * `il_status`는 이력 전체를 보관하므로 셀에는 컨테이너(child_id)마다 최신 1건만 남긴다 — 같은
 * 묶음에서는 event_date가 늦은 것, 같은 날짜면 나중에 등록된 것(id 큰 것). HBL 전체에 걸린
 * status(child_id 0)는 컨테이너가 아니므로 HBL별로 한 줄을 따로 남긴다. 결과는 담당자가 읽는
 * 순서(HBL → 그 HBL의 경고 → 컨테이너)로 정렬한다.
 *
 * @param {Array} statusList Redux에 적재된 status 전체 목록
 * @param {object} options
 * @param {number} options.parentId 대상 shipment(메인 행) id
 * @param {number} options.shipmentType 현재 탭의 shipment_type
 * @returns {Array} 컨테이너별(HBL 레벨은 HBL별) 최신 status 레코드 배열
 */
export const getLatestStatusList = (statusList, { parentId, shipmentType }) => {
    const matchedStatus = statusList.filter(
        (item) => item.shipment_type === shipmentType && item.parent_id === parentId && item.hidden === false,
    );

    const latestByBucket = new Map();
    matchedStatus.forEach((curr) => {
        const key = bucketKeyOf(curr);
        const existing = latestByBucket.get(key);

        if (!existing) {
            latestByBucket.set(key, curr);
            return;
        }

        // event_date is already a yyyy-mm-dd string, so a plain
        // lexicographic compare is the same as a date compare.
        if (existing.event_date < curr.event_date) {
            latestByBucket.set(key, curr);
        } else if (existing.event_date === curr.event_date && existing.id < curr.id) {
            latestByBucket.set(key, curr);
        }
    });

    return sortStatusCellRows(Array.from(latestByBucket.values()));
};
